'use strict';

import config from '../utils/config';
import logger from '../utils/logger';
import layout from '../ui/layout';
import postcodeFormatting from '../utils/postcode-formatting';
import user from '../profile/user';
import adSlotSettings from './ad-slot-settings';
import ozoneProvider from './providers/ozone-provider';

let providers = [ozoneProvider];
let adConfig, googletag, adUnitPath, slotSettings;
let adSlots = {};
let definedSlots = [];

const events = {
	onInit: 'onInit',
	onDefineSlot: 'onDefineSlot',
	onRemoveSlot: 'onRemoveSlot',
	onRemoveAllSlots: 'onRemoveAllSlots',
	onRequestAds: 'onRequestAds'
};

const classNames = {
	advert: 'advert',
	fullScreenGallery: 'full-screen-gallery',
	leaderboardAd: 'leaderboard-ad',
	adContainer: 'advert-container',
	siteRoot: 'site-root',
	targetingUpdateData: 'gpt-targeting-update-data'
};

const modifierNames = {
	loaded: 'loaded',
	viewed: 'viewed',
	heroSkin: 'has-hero-skin',
	fullWidth: 'full-width'
};

const queryStringKeys = {
	adTest: 'adtest',
	itmSource: 'itm_source'
};

const targetingKeys = {
	adTest: 'adtest',
	cmsTemplateName: 'cms_template_name',
	onboarding: 'onboarding',
	position: 'position',
	postcode: 'postcode',
	referrer: 'referrer',
	url: 'url',
	viewport: 'viewport',
	pageviewId: 'pageview_id',
	paidCampaign: 'paidcampaign',
	paidTraffic: 'paidtraffic',
	googleThirdPartyCookieDeprecation: 'google_tpc'
};

const setAdContainerHeight = ($slotElement, eventHeight) => {
	const $adContainer = $slotElement.closestByBem(classNames.adContainer);
	if ($adContainer.length === 0) {
		return;
	}

	const scrollMessageHeight = 40;
	const displayScrollMessage = $adContainer.hasBemClass(classNames.adContainer, null, modifierNames.fullWidth);

	let advertHeight = eventHeight;
	if (displayScrollMessage) {
		advertHeight += scrollMessageHeight;
	}

	$adContainer.css({
		'height': advertHeight
	});

	$slotElement.css({
		'height': advertHeight
	});
};

const eventHandlers = {
	onSlotRenderEnded: (slotId, event) => {
		const $slotElement = getSlotElement(slotId);

		if (event.size) {
			setAdContainerHeight($slotElement, event.size[1]);
		}
	},

	onSlotOnLoad: (slotId) => {
		getSlotElement(slotId).addBemClass(classNames.advert, null, modifierNames.loaded);
	},

	onImpressionViewable: (slotId) => {
		getSlotElement(slotId).addBemClass(classNames.advert, null, modifierNames.viewed);
	}
};

const callbacks = {
	// Note that no provider awaiting has been implemented, it needs to be if we add a second provider that calls
	// one of these callbacks - otherwise we would call it multiple times, each time a provider completes.
	requestAds: (slotsToRefresh) => {
		if (slotsToRefresh) {
			googletag.pubads().refresh(slotsToRefresh);
		} else {
			googletag.pubads().refresh();
		}
	},

	initComplete: () => {
		setVisibility();
		initInjectedMpus();

		addGoogleTagEventListeners();
		defineAndLoadInitialAds();
	}
};

const fireProviderEvent = (event, eventData, callback) => {
	const eventProviders = providers.filter((provider) => {
		return provider[event];
	});

	if (eventProviders.length > 0) {
		const data = {
			eventData: eventData,
			onCompleteCallback: callback
		};

		eventProviders.forEach((provider) => {
			provider[event](data);
		});
	} else if (callback) {
		// Fire callback immediately if no providers implement it
		callback(eventData);
	}
};

const stringIsNullishOrWhiteSpace = (stringValue) => {
	return stringValue === null || stringValue === undefined || typeof (stringValue) !== 'string' || stringValue.trim() === '';
};

const getPagePath = () => {
	let pagePath = window.location.pathname.replace(/^\/|\/$/g, '');

	if (!pagePath) {
		pagePath = 'homepage';
	}

	return pagePath;
};

const defineAdSlot = (adSlot) => {
	let slotSize, sizeMapping;
	const $slotElement = getSlotElement(adSlot.id);

	const overrideSizeMapping = $slotElement.data('size-mapping');
	const overrideSizes = $slotElement.data('sizes');

	if (overrideSizeMapping && overrideSizes) {
		slotSize = overrideSizes;
		sizeMapping = overrideSizeMapping;
	} else if (isSlotFlexible(adSlot)) {
		slotSize = adSlotSettings.flexibleSlotSettings.sizes;
		sizeMapping = adSlotSettings.flexibleSlotSettings.sizeMapping;
	} else {
		slotSize = layout.isDesktop() ? adSlot.sizes.desktop : adSlot.sizes.mobile;
		sizeMapping = adSlot.sizes.sizeMapping;
	}

	const templateName = adConfig.templateName.split('.').pop();
	const path = adUnitPath + '/' + adConfig.adPathSection + '/' + adSlot.id + '/' + templateName;
	const slot = googletag.defineSlot(path, slotSize, adSlot.id)
		.setTargeting(targetingKeys.position, [adSlot.position])
		.addService(googletag.pubads());

	if (sizeMapping) {
		slot.defineSizeMapping(sizeMapping);
	}

	definedSlots.push(adSlot.id);
	return slot;
};

const defineAndLoadInitialAds = () => {
	const pagePath = getPagePath();

	adUnitPath = '/24156345/' + adConfig.site;

	fireProviderEvent(events.onDefineSlot, slotSettings.leaderboard1);
	fireProviderEvent(events.onDefineSlot, slotSettings.inlineFooter);
	fireProviderEvent(events.onDefineSlot, slotSettings.overlay);

	isSlotFlexible(slotSettings.mpu1);

	googletag.cmd.push(function () {
		logger.debug('GPT: Starting initial slot definition');

		adSlots.leaderboard1 = defineAdSlot(slotSettings.leaderboard1);
		adSlots.inlineFooter = defineAdSlot(slotSettings.inlineFooter);
		adSlots.overlay = defineAdSlot(slotSettings.overlay);

		for (let i = 1; i <= adConfig.maxMpuCount; i++) {
			const mpu = 'mpu' + i;
			const mpuSettings = slotSettings[mpu];

			if (isSlotVisible(mpuSettings)) {
				fireProviderEvent(events.onDefineSlot, mpuSettings);
				adSlots[mpu] = defineAdSlot(mpuSettings);
			}
		}

		if (isSlotVisible(slotSettings.native01)) {
			adSlots.native01 = defineAdSlot(slotSettings.native01);
		}

		if (isSlotVisible(slotSettings.native02)) {
			adSlots.native02 = defineAdSlot(slotSettings.native02);
		}

		if (isSlotVisible(slotSettings.seedTag)) {
			adSlots.seedTag = defineAdSlot(slotSettings.seedTag);
		}

		if (isSlotVisible(slotSettings.gumGum)) {
			adSlots.gumGum = defineAdSlot(slotSettings.gumGum);
		}

		setTargeting(pagePath);

		googletag.pubads().enableSingleRequest();

		googletag.pubads().enableLazyLoad({
			renderMarginPercent: 25,
			mobileScaling: 2.0
		});

		googletag.cmd.push(function () {
			// _pdfps stands for Permutive DFP Storage
			if (googletag.pubads().getTargeting('permutive').length === 0) {
				const keyValuePairs = window.localStorage.getItem('_pdfps');
				googletag.pubads().setTargeting('permutive', keyValuePairs ? JSON.parse(keyValuePairs) : []);
			}
		});

		googletag.cmd.push(function () {
			if (googletag.pubads().getTargeting('covatic').length === 0) {
				var keyValuePairs = window.localStorage.getItem('_audienceProfiles');
				googletag.pubads().setTargeting('covatic', keyValuePairs || [])
			}
		})

		googletag.enableServices();

		for (let i = 0; i < definedSlots.length; i++) {
			googletag.display(definedSlots[i]);
		}

		logger.debug('GPT: Defined initial slots');

		fireProviderEvent(events.onRequestAds, null, callbacks.requestAds);
	});
};

const defineAndLoadGalleryAds = () => {
	fireProviderEvent(events.onRemoveAllSlots);

	googletag.cmd.push(function () {
		logger.debug('GPT: Starting gallery slot load');
		let slotsToRefresh = [];

		if (definedSlots.indexOf('leaderboard-gallery') === -1) {
			fireProviderEvent(events.onDefineSlot, slotSettings.leaderboardGallery);

			logger.debug('GPT: Defining leaderboard-gallery slot');
			adSlots.leaderboardGallery = defineAdSlot(slotSettings.leaderboardGallery);
			googletag.display(slotSettings.leaderboardGallery.id);
		}

		// Parkers currently requests this each time, even tho it seems to refresh itself automatically somehow anyway
		slotsToRefresh.push(adSlots.leaderboardGallery);

		if (isSlotVisible(slotSettings.mpuGallery)) {
			if (definedSlots.indexOf('mpu-gallery') === -1) {
				fireProviderEvent(events.onDefineSlot, slotSettings.mpuGallery);

				logger.debug('GPT: Defining mpu-gallery slot');
				adSlots.mpuGallery = defineAdSlot(slotSettings.mpuGallery);
				googletag.display(slotSettings.mpuGallery.id);
			}

			slotsToRefresh.push(adSlots.mpuGallery);
		}

		fireProviderEvent(events.onRequestAds, slotsToRefresh, callbacks.requestAds);

		logger.debug('GPT: Loaded gallery slots');
	});
};

const defineAndLoadResultAds = () => {
	googletag.cmd.push(function () {
		logger.debug('GPT: Starting result slot load');
		const slotsToRefresh = [adSlots.leaderboard1, adSlots.inlineFooter, adSlots.overlay];

		for (let i = 1; i <= adConfig.maxMpuCount; i++) {
			const mpuId = 'mpu-' + i;
			const mpuSlotKey = 'mpu' + i;
			const isVisible = isSlotVisible(slotSettings[mpuSlotKey]);

			if (isVisible) {
				if (definedSlots.indexOf(mpuId) === -1) {
					const mpuSlotSettings = slotSettings[mpuSlotKey];
					fireProviderEvent(events.onDefineSlot, mpuSlotSettings);

					logger.debug('GPT: Defining ' + mpuId + ' slot');
					adSlots[mpuSlotKey] = defineAdSlot(mpuSlotSettings);
					googletag.display(mpuSlotSettings.id);
				}

				slotsToRefresh.push(adSlots[mpuSlotKey]);
			}
		}

		if (isSlotVisible(slotSettings.native01)) {
			if (definedSlots.indexOf(slotSettings.native01.id) === -1) {
				fireProviderEvent(events.onDefineSlot, slotSettings.native01);

				logger.debug('GPT: Defining native-01 slot');
				adSlots.native01 = defineAdSlot(slotSettings.native01);
				googletag.display(slotSettings.native01.id);
			}

			slotsToRefresh.push(adSlots.native01);
		}

		if (isSlotVisible(slotSettings.native02)) {
			if (definedSlots.indexOf(slotSettings.native02.id) === -1) {
				fireProviderEvent(events.onDefineSlot, slotSettings.native02);

				logger.debug('GPT: Defining native-02 slot');
				adSlots.native02 = defineAdSlot(slotSettings.native02);
				googletag.display(slotSettings.native02.id);
			}

			slotsToRefresh.push(adSlots.native02);
		}

		fireProviderEvent(events.onRequestAds, slotsToRefresh, callbacks.requestAds);
		logger.debug('GPT: Loaded result slots');
	});
};

const setTargeting = (pagePath) => {
	for (let key in adConfig.targetingValues) {
		if (Object.prototype.hasOwnProperty.call(adConfig.targetingValues, key)) {
			const value = adConfig.targetingValues[key];

			googletag.pubads().setTargeting(key, [value]);
		}
	}

	const screenSize = layout.getScreenSize();
	googletag.pubads().setTargeting(targetingKeys.viewport, [screenSize]);

	pagePath = '/' + pagePath + '/';
	googletag.pubads().setTargeting(targetingKeys.url, [pagePath]);

	googletag.pubads().setTargeting(targetingKeys.onboarding, [(config.onboardingCandidate === true ? 'yes' : 'no')]);

	const postcode = user.getPostcode();
	if (!stringIsNullishOrWhiteSpace(postcode)) {
		const postcodeOutCode = postcodeFormatting.getOutCode(postcode);
		googletag.pubads().setTargeting(targetingKeys.postcode, postcodeOutCode);
	}

	if (!stringIsNullishOrWhiteSpace(document.referrer)) {
		googletag.pubads().setTargeting(targetingKeys.referrer, [document.referrer]);
	}

	const searchParams = new window.URLSearchParams(window.location.search);

	if (searchParams.has(queryStringKeys.adTest)) {
		const adTestValue = searchParams.get(queryStringKeys.adTest);

		if (!stringIsNullishOrWhiteSpace(adTestValue)) {
			googletag.pubads().setTargeting(targetingKeys.adTest, [adTestValue]);
		}
	}

	googletag.pubads().setTargeting(targetingKeys.pageviewId, config.pageViewId);

	let paidTrafficValue = 'no';

	if (config.ppcTrackingParams) {
		const params = config.ppcTrackingParams;
		const paidCampaignValue = `${params.utmMedium}|${params.utmSource}|${params.trackingIdName},${params.trackingId}`;
		googletag.pubads().setTargeting(targetingKeys.paidCampaign, paidCampaignValue);

		paidTrafficValue = 'yes';
	}

	googletag.pubads().setTargeting(targetingKeys.paidTraffic, paidTrafficValue);

	// Chrome third party cookie deprecation test
	if ('cookieDeprecationLabel' in navigator) {
		navigator.cookieDeprecationLabel.getValue().then((label) => {
			googletag.pubads().setTargeting(targetingKeys.googleThirdPartyCookieDeprecation, label);
		});
	}

	logger.debug('GPT: Targetting set - after ' + logger.secondsSincePageStart());
};

const addGoogleTagEventListeners = () => {
	googletag.cmd.push(function () {
		googletag.pubads().addEventListener('slotRenderEnded', function (event) {
			const slotElementId = event.slot.getSlotElementId();
			logger.debug('GPT: slotRenderEnded fired for slot ' + slotElementId);

			eventHandlers.onSlotRenderEnded(slotElementId, event);
		});

		googletag.pubads().addEventListener('slotOnload', function (event) {
			const slotElementId = event.slot.getSlotElementId();
			logger.debug('GPT: slotOnload fired for slot ' + slotElementId);

			eventHandlers.onSlotOnLoad(slotElementId);
		});

		googletag.pubads().addEventListener('impressionViewable', function (event) {
			const slotElementId = event.slot.getSlotElementId();
			logger.debug('GPT: impressionViewable fired for slot ' + slotElementId);

			eventHandlers.onImpressionViewable(slotElementId);
		});
	});
};

const getSlotElement = (id) => {
	return $('#' + id);
};

const isSlotVisible = (slotSettings) => {
	return getSlotElement(slotSettings.id).is(':visible');
};

const isSlotFlexible = (slotSettings) => {
	return getSlotElement(slotSettings.id).data('is-flexible');
};

const setVisibility = ($context) => {
	$('.advert[data-id]', $context).each(function () {
		const $adContainer = $(this);
		if ($adContainer.is(':visible')) {
			$adContainer.attr('id', $adContainer.attr('data-id'));
		} else {
			$adContainer.removeAttr('id');
		}
	});
};

const initInjectedMpus = () => {
	const $mpuInjectionPoints = $('.mpu-inject').filter(':visible');
	const initialInjectedAdIndex = $('.mpu-ad').filter(':visible').length + 1;

	// Add an adslot to each injection point
	$mpuInjectionPoints.each(function (index, element) {
		const adIndex = index + initialInjectedAdIndex;
		const $injectionElement = $(element);
		const maxAdInjections = adConfig.maxMpuCount ?? 0;

		if (adIndex > maxAdInjections) {
			$injectionElement.hide();
			return;
		}

		const slotIndexName = 'mpu' + adIndex;
		const slot = slotSettings[slotIndexName];
		const $slotElement = $injectionElement.find('.advert');
		$slotElement.addClass('mpu-ad').attr('id', slot.id);
	});
};

export default {
	init: (config) => {
		logger.debug('GPT: Initialied after ' + logger.secondsSincePageStart());
		adConfig = config;

		slotSettings = adSlotSettings.getSlotSettings(adConfig.maxMpuCount);

		googletag = window.googletag = window.googletag || {};
		googletag.cmd = googletag.cmd || [];
		googletag.cmd.push(function () {
			googletag.pubads().disableInitialLoad();
		});

		fireProviderEvent(events.onInit, adConfig, callbacks.initComplete);
	},

	setupHeroSkin: function (options) {
		$.findByBem(classNames.siteRoot).addBemClass(classNames.siteRoot, null, modifierNames.heroSkin);

		if (!options || !options.clickUrl) {
			return;
		}

		let $overlay = $('#' + slotSettings.overlay.id);
		$overlay.wrap('<a href="' + options.clickUrl + '" target="_blank"></a>');

		let image = options.mobileImage;
		const screenSize = layout.getScreenSize();

		switch (screenSize) {
			case 'XL':
			case 'XXL':
				image = options.desktopImage;
				break;
			case 'L':
			case 'M':
				image = options.tabletImage;
				break;
			default:
				image = options.mobileImage;
				break;
		}

		$overlay.css({ 'background-image': 'url("' + image + '")' });
	},

	loadGalleryAds: () => {
		const $context = $.findByBem(classNames.fullScreenGallery);
		setVisibility($context);
		defineAndLoadGalleryAds();
	},

	loadResultAds: () => {
		if (adConfig) {
			setVisibility();
			initInjectedMpus();
			defineAndLoadResultAds();
		}
	},

	updateCmsTemplateName: (templateName) => {
		if (adConfig.suppressAllAds) {
			return;
		}
		googletag.cmd.push(() => {
			googletag.pubads().setTargeting(targetingKeys.cmsTemplateName, [templateName ?? adConfig.targetingValues[targetingKeys.cmsTemplateName]]);
		});
	},

	updateTargeting: () => {
		googletag.pubads().clearTargeting();

		const updateData = $.findByBem(classNames.targetingUpdateData).attr('data-value');

		if (updateData && adConfig) {
			try {
				adConfig.targetingValues = JSON.parse(updateData);
				const pagePath = getPagePath();
				setTargeting(pagePath);
			} catch (e) {
				logger.warn('GPT: Invalid dfp data json', e);
			}
		}
	}
};
